import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import {
  fetchMatch,
  fetchCaptionBuilderConfig,
  updateMatch,
  pinMatchQuickCaption,
  unpinMatchQuickCaption,
} from '../../../../api';
import {
  addPendingPin,
  dropPendingPin,
  mergeServerAndPending,
  replacePendingPin,
} from '../../../../utils/quickCaptionPinsCache';
import styles from './CaptionBuilder.module.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getTeamAbbr(teamName, prefixes, shortName) {
  if (shortName) return shortName.toUpperCase();
  if (!teamName) return '';
  let name = teamName.trim();
  for (const prefix of prefixes) {
    const re = new RegExp(`^${prefix}\\s+`, 'i');
    if (re.test(name)) {
      name = name.replace(re, '').trim();
      break;
    }
  }
  return name.slice(0, 3).toUpperCase();
}

function teamColor(teamName) {
  if (!teamName) return '#888';
  let hash = 0;
  for (let i = 0; i < teamName.length; i++) {
    hash = teamName.charCodeAt(i) + ((hash << 5) - hash);
  }
  return `hsl(${Math.abs(hash) % 360}, 60%, 55%)`;
}

function formatScore(home, away) {
  return `${home}-${away}`;
}

function buildCaption(chain, score, teamPrefixes) {
  return chain
    .map((node) => {
      if (node.type === 'person') {
        const teamName = node.person.team_memberships?.[0]?.team_name;
        const teamShortName = node.person.team_memberships?.[0]?.team_short_name;
        const fullName = teamName
          ? `${node.person.display_name} of ${teamName}`
          : node.person.display_name;
        if (node.action) {
          let tmpl = node.action.template;
          if (node.action.needsScore) {
            tmpl = tmpl.replace('{score}', score);
          }
          tmpl = tmpl.replace(/\{team\}/g, teamName || '');
          tmpl = tmpl.replace(
            /\{team_abbr\}/g,
            getTeamAbbr(teamName, teamPrefixes || [], teamShortName)
          );
          // Strip angle-bracket optional markers: <text> becomes text
          tmpl = tmpl.replace(/<([^>]+)>/g, (_, inner) => inner);
          return `${fullName} ${tmpl}`;
        }
        return fullName;
      }
      return node.text || '';
    })
    .join(' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function collectKeywords(chain) {
  const kws = new Set();
  for (const node of chain) {
    if (node.type === 'person' && node.action) {
      for (const kw of node.action.keywords) {
        kws.add(kw);
      }
    }
  }
  return [...kws];
}

function checkGrammar(text, rules) {
  const warnings = [];
  for (const rule of rules) {
    if (rule.pattern.test(text)) {
      warnings.push(rule.message);
    }
  }
  return warnings;
}

function isChainComplete(chain) {
  if (chain.length === 0) return false;
  const persons = chain.filter((n) => n.type === 'person');
  if (persons.length === 0) return false;
  if (!persons.some((p) => p.action)) return false;
  const connectors = chain.filter((n) => n.type === 'connector');
  if (connectors.some((c) => !c.text.trim())) return false;
  return true;
}

function serializeChains(savedChains, score) {
  return {
    chains: savedChains.map((sc) => ({
      chain: sc.chain.map((node) =>
        node.type === 'person'
          ? { type: 'person', person_id: node.person.id, action_id: node.action?.id || null }
          : { type: 'connector', text: node.text }
      ),
    })),
    score,
  };
}

function deserializeChains(data, persons, actionCategories) {
  if (!data?.chains) return null;
  const personMap = new Map(persons.map((p) => [p.id, p]));
  const actionMap = new Map();
  for (const cat of actionCategories) {
    for (const a of cat.actions) actionMap.set(a.id, a);
  }
  const restored = [];
  for (const sc of data.chains) {
    const chain = [];
    let valid = false;
    for (const node of sc.chain) {
      if (node.type === 'person') {
        const person = personMap.get(node.person_id);
        if (!person) continue;
        const action = node.action_id ? actionMap.get(node.action_id) : null;
        if (node.action_id && !action) continue;
        chain.push({ type: 'person', person, action });
        valid = true;
      } else {
        chain.push({ type: 'connector', text: node.text });
      }
    }
    if (valid) restored.push(chain);
  }
  return { chains: restored, score: data.score || [0, 0] };
}

// ---------------------------------------------------------------------------
// ScoreTicker
// ---------------------------------------------------------------------------

// Compact score editor surfaced in the inline trigger row, outside the
// drawer. Bound to the same homeScore / awayScore state ScoreTicker
// uses; the existing 600 ms debounced match PATCH covers persistence.
function MiniScore({ matchData, homeScore, awayScore, onHomeChange, onAwayChange, teamPrefixStrip }) {
  if (!matchData) return null;
  const homeAbbr = getTeamAbbr(matchData.home_team_name, teamPrefixStrip);
  const awayAbbr = getTeamAbbr(matchData.away_team_name, teamPrefixStrip);
  return (
    <div className={styles.miniScore}>
      <span className={styles.miniTeam}>{homeAbbr}</span>
      <button
        type="button"
        className={styles.miniBtn}
        onClick={() => onHomeChange(Math.max(0, homeScore - 1))}
        aria-label="Decrease home score"
      >
        {'−'}
      </button>
      <span className={styles.miniNum}>{homeScore}</span>
      <button
        type="button"
        className={styles.miniBtn}
        onClick={() => onHomeChange(homeScore + 1)}
        aria-label="Increase home score"
      >
        {'+'}
      </button>
      <span className={styles.miniSep}>:</span>
      <button
        type="button"
        className={styles.miniBtn}
        onClick={() => onAwayChange(Math.max(0, awayScore - 1))}
        aria-label="Decrease away score"
      >
        {'−'}
      </button>
      <span className={styles.miniNum}>{awayScore}</span>
      <button
        type="button"
        className={styles.miniBtn}
        onClick={() => onAwayChange(awayScore + 1)}
        aria-label="Increase away score"
      >
        {'+'}
      </button>
      <span className={styles.miniTeam}>{awayAbbr}</span>
    </div>
  );
}

function ScoreTicker({ matchData, homeScore, awayScore, onHomeChange, onAwayChange, teamPrefixStrip }) {
  if (!matchData) return null;

  const homeAbbr = getTeamAbbr(matchData.home_team_name, teamPrefixStrip);
  const awayAbbr = getTeamAbbr(matchData.away_team_name, teamPrefixStrip);

  return (
    <div className={styles.scoreTicker}>
      <span className={styles.teamAbbr}>{homeAbbr}</span>
      <button
        className={styles.scoreBtn}
        onClick={() => onHomeChange(Math.max(0, homeScore - 1))}
      >
        {'\u2212'}
      </button>
      <span className={styles.scoreNum}>{homeScore}</span>
      <button className={styles.scoreBtn} onClick={() => onHomeChange(homeScore + 1)}>
        {'+'}
      </button>
      <span className={styles.scoreSeparator}>:</span>
      <button
        className={styles.scoreBtn}
        onClick={() => onAwayChange(Math.max(0, awayScore - 1))}
      >
        {'\u2212'}
      </button>
      <span className={styles.scoreNum}>{awayScore}</span>
      <button className={styles.scoreBtn} onClick={() => onAwayChange(awayScore + 1)}>
        {'+'}
      </button>
      <span className={styles.teamAbbr}>{awayAbbr}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PlayerPool
// ---------------------------------------------------------------------------

function PlayerPool({ persons, chain, onAdd, teamPrefixStrip }) {
  const personIdsInChain = useMemo(() => {
    const ids = new Set();
    for (const node of chain) {
      if (node.type === 'person') ids.add(node.person.id);
    }
    return ids;
  }, [chain]);

  if (!persons || persons.length === 0) {
    return <span className={styles.emptyPool}>No persons detected in image</span>;
  }

  return (
    <div className={styles.playerPool}>
      {persons.map((p) => {
        const inChain = personIdsInChain.has(p.id);
        const teamName = p.team_memberships?.[0]?.team_name;
        const teamShortName = p.team_memberships?.[0]?.team_short_name;
        const abbr = getTeamAbbr(teamName, teamPrefixStrip, teamShortName);
        const color = teamColor(teamName);
        return (
          <button
            key={p.id}
            className={clsx(styles.playerChip, inChain && styles.playerChipInChain)}
            onClick={() => onAdd(p)}
          >
            <span className={styles.teamDot} style={{ background: color }} />
            <span>{p.display_name}</span>
            {abbr && <span className={styles.chipTeam}>{abbr}</span>}
            {inChain && (
              <svg className={styles.chipCheck} viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActionPicker
// ---------------------------------------------------------------------------

function ActionPicker({ selectedActionId, onSelect, onClose, actionCategories }) {
  const [activeTab, setActiveTab] = useState(actionCategories[0]?.id);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  if (actionCategories.length === 0) return null;

  const category = actionCategories.find((c) => c.id === activeTab) || actionCategories[0];

  return (
    <div className={styles.actionPicker} ref={ref}>
      <div className={styles.actionTabs}>
        {actionCategories.map((cat) => (
          <button
            key={cat.id}
            className={clsx(styles.actionTab, activeTab === cat.id && styles.actionTabActive)}
            onClick={() => setActiveTab(cat.id)}
          >
            {cat.label}
          </button>
        ))}
      </div>
      <div className={styles.actionGrid}>
        {category.actions.map((action) => (
          <button
            key={action.id}
            className={clsx(
              styles.actionOption,
              selectedActionId === action.id && styles.actionOptionSelected
            )}
            onClick={() => onSelect(action)}
          >
            {action.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ConnectorEditor
// ---------------------------------------------------------------------------

function ConnectorEditor({ text, precedingActionId, onChange, onRemove, connectorSuggestions }) {
  const [focused, setFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapRef = useRef(null);

  const suggestions =
    connectorSuggestions[precedingActionId] || connectorSuggestions.default;

  useEffect(() => {
    function handleClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className={styles.connectorRow}>
      <div className={styles.connectorLine} />
      <div className={styles.connectorInputWrap} ref={wrapRef}>
        <input
          className={styles.connectorInput}
          type="text"
          value={text}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => {
            setFocused(true);
            if (!text) setShowSuggestions(true);
          }}
          onBlur={() => setFocused(false)}
          placeholder="connector..."
          style={{ width: Math.max(80, text.length * 7 + 24) }}
        />
        {showSuggestions && (
          <div className={styles.suggestions}>
            {suggestions.map((s) => (
              <button
                key={s}
                className={styles.suggestionBtn}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(s);
                  setShowSuggestions(false);
                }}
              >
                {s}
              </button>
            ))}
          </div>
        )}
        <button className={styles.connectorRemove} onClick={onRemove} title="Remove">
          {'\u00d7'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ChainNode
// ---------------------------------------------------------------------------

function ChainNode({ node, index, activePickerIndex, onSetActivePicker, onActionSelect, onRemove, teamPrefixStrip, actionCategories }) {
  const teamName = node.person.team_memberships?.[0]?.team_name;
  const teamShortName = node.person.team_memberships?.[0]?.team_short_name;
  const abbr = getTeamAbbr(teamName, teamPrefixStrip, teamShortName);
  const color = teamColor(teamName);
  const isPickerOpen = activePickerIndex === index;

  return (
    <>
      <div className={styles.chainNode}>
        <div className={styles.nodeColorBar} style={{ background: color }} />
        <div className={styles.nodeInfo}>
          <span className={styles.nodeName}>{node.person.display_name}</span>
          {abbr && <span className={styles.nodeTeam}>{abbr}</span>}
        </div>
        <button
          className={clsx(styles.actionBtn, !node.action && styles.actionBtnEmpty)}
          onClick={() => onSetActivePicker(isPickerOpen ? -1 : index)}
        >
          {node.action ? node.action.label : 'Action...'}
        </button>
        <button className={styles.removeBtn} onClick={() => onRemove(index)} title="Remove">
          {'\u00d7'}
        </button>
      </div>
      {isPickerOpen && (
        <ActionPicker
          selectedActionId={node.action?.id}
          onSelect={(action) => {
            onActionSelect(index, action);
            onSetActivePicker(-1);
          }}
          onClose={() => onSetActivePicker(-1)}
          actionCategories={actionCategories}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// CaptionPreview
// ---------------------------------------------------------------------------

function CaptionPreview({ caption, keywords, grammarRules }) {
  const warnings = useMemo(() => checkGrammar(caption, grammarRules), [caption, grammarRules]);

  return (
    <div className={styles.preview}>
      {warnings.length > 0 &&
        warnings.map((w) => (
          <div key={w} className={styles.grammarWarning}>
            ! {w}
          </div>
        ))}
      <div className={clsx(styles.previewText, !caption && styles.previewEmpty)}>
        {caption || 'Start building a chain to see the preview...'}
      </div>
      {keywords.length > 0 && (
        <div className={styles.previewKeywords}>
          <span className={styles.kwLabel}>Keywords:</span>
          {keywords.map((kw) => (
            <span key={kw} className={styles.kwPill}>
              {kw}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SavedChains
// ---------------------------------------------------------------------------

function SavedChainsList({ savedChains, onRemove }) {
  if (savedChains.length === 0) return null;

  const combined = savedChains.map((sc) => sc.caption).join('. ') + '.';

  return (
    <div className={styles.savedChains}>
      <div className={styles.sectionTitle}>Saved chains ({savedChains.length})</div>
      {savedChains.map((sc, i) => (
        <div key={i} className={styles.savedChain}>
          <span className={styles.savedText}>{sc.caption}</span>
          <button
            className={styles.savedRemove}
            onClick={() => onRemove(i)}
            title="Remove chain"
          >
            {'\u00d7'}
          </button>
        </div>
      ))}
      {savedChains.length > 1 && (
        <>
          <div className={styles.combinedLabel}>Combined output</div>
          <div className={styles.combinedText}>{combined}</div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drawer (portal)
// ---------------------------------------------------------------------------

function Drawer({ open, onClose, children }) {
  const [mounted, setMounted] = useState(false);
  const [entering, setEntering] = useState(true);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setEntering(true);
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => setEntering(false));
      });
      return () => cancelAnimationFrame(raf);
    } else {
      setEntering(true);
      const timer = setTimeout(() => setMounted(false), 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!mounted) return null;

  return createPortal(
    <>
      <div className={styles.backdrop} onClick={onClose} />
      <div className={clsx(styles.drawer, entering && styles.drawerEntering)}>
        {children}
      </div>
    </>,
    document.body
  );
}

// ---------------------------------------------------------------------------
// CaptionBuilder (main export)
// ---------------------------------------------------------------------------

export default function CaptionBuilder({
  imageId,
  matchId,
  persons,
  orderedNames,
  _captionOverride,
  captionChains,
  renderedCaption,
  existingKeywords,
  onCaptionChange,
  onKeywordsChange,
  disabled,
  primarySubjectPersonId,
  capturedAt,
  matchPhase: matchPhaseProp,
  photographer,
}) {
  const selectedPhotoPrimarySubject = primarySubjectPersonId || null;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [matchData, setMatchData] = useState(null);
  const [homeScore, setHomeScore] = useState(0);
  const [awayScore, setAwayScore] = useState(0);
  const [chain, setChain] = useState([]);
  const [savedChains, setSavedChains] = useState([]);
  const [activePickerIndex, setActivePickerIndex] = useState(-1);
  const [builderConfig, setBuilderConfig] = useState(null);
  const [pinNextCaption, setPinNextCaption] = useState(true);
  const [pinnedCaptions, setPinnedCaptions] = useState([]);
  const scoreSyncTimer = useRef(null);
  const scoreInitFromMatch = useRef(false);

  // Fetch caption builder config (independent of match)
  useEffect(() => {
    let cancelled = false;
    fetchCaptionBuilderConfig()
      .then((data) => { if (!cancelled) setBuilderConfig(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Fetch match data
  useEffect(() => {
    if (!matchId) {
      setMatchData(null);
      setPinnedCaptions([]);
      scoreInitFromMatch.current = false;
      return;
    }
    let cancelled = false;
    scoreInitFromMatch.current = false;
    fetchMatch(matchId)
      .then((data) => {
        if (cancelled) return;
        setMatchData(data);
        // Seed local score from persisted match values once per match.
        if (data?.home_score != null) setHomeScore(data.home_score);
        if (data?.away_score != null) setAwayScore(data.away_score);
        scoreInitFromMatch.current = true;
        // Merge server pins with any optimistic in-memory pins from
        // earlier in this session that the server hasn't acknowledged
        // yet — keeps the chip visible across photo navigation when
        // the pin PATCH is still in flight or was CORS-blocked.
        setPinnedCaptions(
          mergeServerAndPending(matchId, data?.quick_captions_pinned),
        );
      })
      .catch(() => {
        if (!cancelled) setMatchData(null);
      });
    return () => {
      cancelled = true;
    };
  }, [matchId]);

  // Debounced PATCH /api/matches/<id>/ on score change. We skip the
  // sync until matchData has seeded local state, otherwise the initial
  // 0/0 would clobber the persisted score.
  useEffect(() => {
    if (!matchId || !scoreInitFromMatch.current) return undefined;
    if (matchData?.home_score === homeScore && matchData?.away_score === awayScore) {
      return undefined;
    }
    if (scoreSyncTimer.current) clearTimeout(scoreSyncTimer.current);
    scoreSyncTimer.current = setTimeout(() => {
      updateMatch(matchId, { home_score: homeScore, away_score: awayScore })
        .then((updated) => {
          setMatchData((prev) => (prev ? { ...prev, ...updated } : updated));
        })
        .catch(() => {});
    }, 600);
    return () => {
      if (scoreSyncTimer.current) clearTimeout(scoreSyncTimer.current);
    };
  }, [homeScore, awayScore, matchId, matchData?.home_score, matchData?.away_score]);

  // Derive config values from builderConfig
  const actionCategories = builderConfig?.action_categories || [];
  const connectorSuggestions = builderConfig?.connector_suggestions || { default: [] };
  const teamPrefixStrip = useMemo(
    () => builderConfig?.team_prefix_strip || [],
    [builderConfig?.team_prefix_strip]
  );

  const grammarRules = useMemo(() => {
    const raw = builderConfig?.grammar_rules || [];
    return raw.map((r) =>
      typeof r.pattern === 'string'
        ? { pattern: new RegExp(r.pattern, 'i'), message: r.message }
        : r
    );
  }, [builderConfig]);

  // Restore chains from persisted captionChains once config is loaded
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current || !builderConfig || !captionChains) return;
    const cats = builderConfig.action_categories || [];
    const result = deserializeChains(captionChains, persons, cats);
    if (!result) return;
    restoredRef.current = true;
    const restoredSaved = result.chains.map((chainNodes) => ({
      caption: buildCaption(chainNodes, formatScore(result.score[0], result.score[1]), teamPrefixStrip),
      keywords: collectKeywords(chainNodes),
      chain: chainNodes,
    }));
    if (restoredSaved.length > 0) {
      setSavedChains(restoredSaved);
      setHomeScore(result.score[0]);
      setAwayScore(result.score[1]);
    }
  }, [builderConfig, captionChains, persons, teamPrefixStrip]);

  // Score string for templates
  const scoreStr = formatScore(homeScore, awayScore);

  // Has at least one face on this photo been tagged? Drives whether
  // {home}/{away} supporters chips render (per spec: when no players
  // tagged, show generic + supporters; otherwise hide supporters).
  const hasTaggedFaces = useMemo(() => {
    if (!Array.isArray(orderedNames)) return false;
    return orderedNames.some((n) => !n.softDeleted);
  }, [orderedNames]);

  // Tagged-face count drives chips that depend on whether the photo
  // has one tagged person (e.g. {persons} dribbling) vs. multiple
  // ({persons} dueling for the ball).
  const taggedFaceCount = useMemo(() => {
    if (!Array.isArray(orderedNames)) return 0;
    return orderedNames.filter((n) => !n.softDeleted).length;
  }, [orderedNames]);

  // Match phase resolution:
  //   1. Server-provided (PhotoSerializer.match_phase) wins — matches
  //      the server-rendered caption's phase exactly.
  //   2. Fallback: derive client-side from matchData.kick_off_time
  //      (the field match.get_match_phase pivots on) +
  //      matchData.full_time_at vs the photo's capture_date. Keeps
  //      phase-tagged chips correct even before the operator restart
  //      that surfaces match_phase on PhotoSerializer.
  //   3. When kick_off_time is unknown we can't decide — return null
  //      so phase-tagged chips stay hidden rather than over-show.
  const matchPhase = useMemo(() => {
    if (matchPhaseProp) return matchPhaseProp;
    if (!capturedAt) return null;
    const cap = new Date(capturedAt).getTime();
    if (Number.isNaN(cap)) return null;
    const kickoff = matchData?.kick_off_time
      ? new Date(matchData.kick_off_time).getTime()
      : null;
    const fullTime = matchData?.full_time_at
      ? new Date(matchData.full_time_at).getTime()
      : null;
    if (!kickoff) return null;
    if (cap < kickoff) return 'before';
    if (fullTime && cap > fullTime + 30 * 60_000) return 'after';
    return 'during';
  }, [matchPhaseProp, capturedAt, matchData]);

  // Build the (primary, others) pair from orderedNames mirroring the
  // backend resolution rule (explicit primary_subject_person_id wins;
  // otherwise first in caption order).
  const personTokenLists = useMemo(() => {
    const active = (orderedNames || []).filter((n) => !n.softDeleted && n.person_id);
    if (active.length === 0) return null;
    const explicitId = selectedPhotoPrimarySubject;
    let primaryIdx = 0;
    if (explicitId) {
      const idx = active.findIndex((n) => String(n.person_id) === String(explicitId));
      if (idx >= 0) primaryIdx = idx;
    }
    const primary = active[primaryIdx];
    const others = active.filter((_, i) => i !== primaryIdx);
    const teamSuffix = (n) => (n.label && n.label !== n.displayName ? n.label : n.displayName || n.label || '');
    const plain = (n) => n.displayName || n.label || '';
    return {
      primaryName: plain(primary),
      primaryTeam: teamSuffix(primary) || plain(primary),
      othersNames: others.map(plain).filter(Boolean),
      othersTeams: others.map((n) => teamSuffix(n) || plain(n)).filter(Boolean),
    };
  }, [orderedNames, selectedPhotoPrimarySubject]);

  // Resolve template variables against match + photo context. Optional
  // `overrides` map wins over the default tokens, used by quick-action
  // phrase substitution (`{persons}` ← resolved phrase).
  const resolveTemplate = useCallback((template, overrides = null) => {
    if (typeof template !== 'string') return '';
    const home = matchData?.home_team_name || '';
    const away = matchData?.away_team_name || '';
    const venue = matchData?.venue || '';
    const matchName =
      matchData?.name ||
      (home && away ? `${home} vs ${away}` : '') ||
      'match';
    const dateObj = matchData?.start_time ? new Date(matchData.start_time) : null;
    const date = dateObj ? dateObj.toLocaleDateString() : '';

    // City / country / season / competition / day / month / monthname /
    // year — sourced from matchData.iptc_config and the start_time.
    // These mirror the backend's build_template_context so company
    // caption_templates render correctly client-side.
    const iptc = matchData?.iptc_config || {};
    const city = iptc.city || matchData?.city || '';
    const state = iptc.state || '';
    const country = iptc.country || matchData?.country || '';
    const location = iptc.location || '';
    const season = matchData?.season || '';
    const competition = matchData?.competition_display || matchData?.competition || '';
    const homeTeam = matchData?.home_team_name || home;
    const awayTeam = matchData?.away_team_name || away;
    const event = matchData?.name
      || (homeTeam && awayTeam ? `${homeTeam} vs ${awayTeam}` : '');
    const team = homeTeam;
    const photog = (() => {
      // PhotoSerializer.get_photographer returns {id, name: <username>}.
      // The backend's iptc_templates uses photo.user.first_name +
      // last_name when available; we fall back to whatever the frontend
      // serializer surfaces (today: username via `name`).
      const p = photographer || matchData?.photographer;
      if (!p) return '';
      const full = `${p.first_name || ''} ${p.last_name || ''}`.trim();
      return full || p.name || p.username || '';
    })();
    const day = dateObj ? String(dateObj.getDate()) : '';
    const day0 = dateObj ? String(dateObj.getDate()).padStart(2, '0') : '';
    const month = dateObj ? String(dateObj.getMonth() + 1).padStart(2, '0') : '';
    const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const monthname = dateObj ? MONTH_NAMES[dateObj.getMonth()] : '';
    const year4 = dateObj ? String(dateObj.getFullYear()) : '';

    // Score + safe phrase. Score state is held as numbers in this
    // component; treat 0-0 as "unset" for the natural-language phrase.
    const hasRealScore = !(homeScore === 0 && awayScore === 0);
    const score = scoreStr || '';
    const scorePhrase = hasRealScore ? `the ${homeScore}-${awayScore}` : 'a goal';

    // Person tokens. Mirrors backend iptc_templates._join_with_and.
    const joinAnd = (parts) => {
      const arr = parts.filter(Boolean);
      if (arr.length === 0) return '';
      if (arr.length === 1) return arr[0];
      return `${arr.slice(0, -1).join(', ')} and ${arr[arr.length - 1]}`;
    };
    const t = personTokenLists;
    const primarySubject = t?.primaryName || '';
    const primarySubjectTeam = t?.primaryTeam || primarySubject;
    const others = t ? t.othersNames.join(', ') : '';
    const othersAnd = t ? joinAnd(t.othersNames) : '';
    const othersTeamAnd = t ? joinAnd(t.othersTeams) : '';
    const allNames = t ? [primarySubject, ...t.othersNames].filter(Boolean) : [];
    const allTeams = t ? [primarySubjectTeam, ...t.othersTeams].filter(Boolean) : [];
    const personsAnd = joinAnd(allNames);
    const personsTeamAnd = joinAnd(allTeams);
    const persons = t ? allNames.join(', ') : '';

    const ctx = {
      home, away, venue, match_name: matchName, score, score_phrase: scorePhrase,
      date, day, day0, month, monthname, year4, season, competition,
      city, state, country, location, event, team, photog,
      home_team: homeTeam, away_team: awayTeam,
      // {match_phase} mirrors the backend default: "during" when we
      // can't derive a phase from capture_date + match times.
      match_phase: matchPhase || 'during',
      primary_subject: primarySubject,
      primary_subject_team: primarySubjectTeam,
      others, others_and: othersAnd, others_team_and: othersTeamAnd,
      persons, persons_and: personsAnd, persons_team_and: personsTeamAnd,
      ...(overrides || {}),
    };

    // Substitute {var} or {var:modifier}. Unknown tokens render as ''.
    const applyMod = (value, modifier) => {
      if (!modifier) return value ?? '';
      const v = value == null ? '' : String(value);
      if (modifier === 'upper') return v.toUpperCase();
      if (modifier === 'lower') return v.toLowerCase();
      if (modifier === 'title') return v.replace(/\w\S*/g, (s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase());
      return v;
    };
    let out = template.replace(/\{(\w+)(?::(\w+))?\}/g, (_match, name, modifier) => {
      const val = Object.prototype.hasOwnProperty.call(ctx, name) ? ctx[name] : '';
      return applyMod(val, modifier);
    });

    // Post-process: drop dangling " with .", collapse double spaces,
    // trim trailing spaces before punctuation. Keeps the caption readable
    // when optional tokens (e.g. {others_and}) resolve to empty.
    out = out.replace(/\s+with\s*([.,;:!?])/g, '$1');
    out = out.replace(/\s+with\s*$/g, '');
    out = out.replace(/\s{2,}/g, ' ');
    out = out.replace(/\s+([.,;:!?])/g, '$1');
    return out.trim();
  }, [matchData, scoreStr, homeScore, awayScore, personTokenLists, photographer, matchPhase]);

  // Company-level + per-match quick actions, filtered by visibility rules.
  // Frontend ships a default set so chips are visible even before the
  // operator migration that seeds Company.iptc_defaults.quick_actions.
  // Phrase-form defaults: each action provides a `phrase` that gets
  // injected into the company's caption_template at the {persons}
  // placeholder. Older `template` field (full caption) remains accepted
  // by applyQuickAction as a fallback when the company hasn't migrated
  // yet, but new defaults all use `phrase`.
  // Phrase-form defaults. Each entry's visibility is controlled by:
  //   requires_faces  — true: only when at least one face is tagged
  //                     false: only when no faces are tagged
  //                     undefined: any
  //   face_count      — 'one'  : exactly one face tagged
  //                     'multi': two or more faces tagged
  //                     undefined: any face count (combined with
  //                     requires_faces it gates the with/without case)
  //   match_phase     — 'before' / 'during' / 'after' / undefined
  //                     filters by photo's capture_date vs match.
  //   keywords        — optional list of IPTC keywords merged into the
  //                     photo's keywords_override when the chip is
  //                     applied (additive, deduped).
  // The filter (`quickActions` memo below) honours visibility predicates;
  // applyQuickAction merges keywords on click.
  const FALLBACK_QUICK_ACTIONS = [
    // Without faces (any phase)
    { id: 'general_overview',         label: 'General overview of {venue}',     phrase: 'General overview of {venue}',                                            requires_faces: false, keywords: ['general view'] },
    { id: 'match_balls',              label: 'Match balls',                     phrase: 'Match ball at {venue}',                                                  requires_faces: false, keywords: ['match ball'] },
    { id: 'home_supporters',          label: '{home} supporters',               phrase: '{home} supporters',                                                      requires_faces: false, keywords: ['supporters', 'fans'] },
    { id: 'away_supporters',          label: '{away} supporters',               phrase: '{away} supporters',                                                      requires_faces: false, keywords: ['supporters', 'fans'] },
    { id: 'home_thanking_home_supps', label: '{home} thanking own supporters',  phrase: '{home} thanking {home} supporters',                                      requires_faces: false, keywords: ['supporters', 'thanking'] },
    { id: 'away_thanking_away_supps', label: '{away} thanking own supporters',  phrase: '{away} thanking {away} supporters',                                      requires_faces: false, keywords: ['supporters', 'thanking'] },

    // With faces — gated to match_phase='during'. These all describe
    // in-match moments (action / goal / thanking the crowd) so they
    // don't make sense pre-kickoff or post-final-whistle.
    { id: 'match_action',             label: 'Match action',                    phrase: '{primary_subject_team} in action with {others_team_and}',                requires_faces: true, match_phase: 'during', keywords: ['action'] },
    { id: 'goal_celebration',         label: 'Goal celebration',                phrase: '{primary_subject_team} celebrating {score_phrase} with {others_team_and}', requires_faces: true, match_phase: 'during', keywords: ['goal', 'celebration'] },
    { id: 'persons_thanking_home',    label: 'Thanking {home} supporters',      phrase: '{primary_subject_team} thanking {home} supporters with {others_team_and}', requires_faces: true, match_phase: 'during', keywords: ['thanking', 'supporters'] },
    { id: 'persons_thanking_away',    label: 'Thanking {away} supporters',      phrase: '{primary_subject_team} thanking {away} supporters with {others_team_and}', requires_faces: true, match_phase: 'during', keywords: ['thanking', 'supporters'] },

    // With faces — phase-conditioned, count-conditioned scene phrases.
    { id: 'coach_interview',          label: 'Giving an interview',             phrase: '{primary_subject_team} giving an interview',                             requires_faces: true, face_count: 'one', match_phase: 'before', requires_role: 'coach', keywords: ['interview', 'coach'] },
    { id: 'entering_pitch',           label: 'Entering the pitch',              phrase: '{primary_subject_team} entering the pitch with {others_team_and}',       requires_faces: true, match_phase: 'before', keywords: ['pre-match', 'entering pitch'] },
    { id: 'warming_up',               label: 'Warming up',                      phrase: '{primary_subject_team} warming up with {others_team_and}',               requires_faces: true, match_phase: 'before', keywords: ['pre-match', 'warm-up'] },
    { id: 'looks_on',                 label: 'Looks on',                        phrase: '{primary_subject_team} looks on',                                        requires_faces: true, face_count: 'one' },
    { id: 'dribbling',                label: 'Dribbling',                       phrase: '{primary_subject_team} dribbling',                                       requires_faces: true, face_count: 'one',   match_phase: 'during', keywords: ['action', 'dribble'] },
    { id: 'passing',                  label: 'Passing',                         phrase: '{primary_subject_team} passing',                                         requires_faces: true, face_count: 'one',   match_phase: 'during', keywords: ['action', 'pass'] },
    { id: 'shooting',                 label: 'Shooting',                        phrase: '{primary_subject_team} shooting',                                        requires_faces: true, face_count: 'one',   match_phase: 'during', keywords: ['action', 'shot'] },
    { id: 'dueling_for_ball',         label: 'Dueling for the ball',            phrase: '{primary_subject_team} dueling for the ball with {others_team_and}',     requires_faces: true, face_count: 'multi', match_phase: 'during', keywords: ['action', 'duel'] },
    { id: 'battling_for_possession',  label: 'Battling for possession',         phrase: '{primary_subject_team} battling for possession with {others_team_and}',  requires_faces: true, face_count: 'multi', match_phase: 'during', keywords: ['action', 'possession'] },

    // With faces — incidents. Single-person verbs drop "with" entirely
    // (cards / injuries are individual events). "Talking" pairs.
    { id: 'injured',                  label: 'Injured',                         phrase: '{primary_subject_team} injured',                                         requires_faces: true, keywords: ['injury'] },
    { id: 'stretchered_off',          label: 'Stretchered off',                 phrase: '{primary_subject_team} is stretchered off the pitch',                    requires_faces: true, keywords: ['injury', 'stretcher'] },
    { id: 'yellow_card',              label: 'Yellow card',                     phrase: '{primary_subject_team} receiving a yellow card',                         requires_faces: true, match_phase: 'during', keywords: ['yellow card', 'caution', 'booking'] },
    { id: 'red_card',                 label: 'Red card',                        phrase: '{primary_subject_team} receiving a red card',                            requires_faces: true, match_phase: 'during', keywords: ['red card', 'sending off'] },
    { id: 'talking',                  label: 'Talking',                         phrase: '{primary_subject_team} talking with {others_team_and}',                  requires_faces: true },
  ];

  const quickActions = useMemo(() => {
    const fromConfig = Array.isArray(builderConfig?.quick_actions)
      ? builderConfig.quick_actions
      : null;
    const fromMatch = Array.isArray(matchData?.company?.iptc_defaults?.quick_actions)
      ? matchData.company.iptc_defaults.quick_actions
      : null;
    const companyActions = fromConfig || fromMatch || FALLBACK_QUICK_ACTIONS;

    // Coach detection: today coaches are prefixed with "Coach " in their
    // display name. When face_count is one, the primary subject is the
    // single tagged person; check their name for the prefix.
    const primaryName = personTokenLists?.primaryName || '';
    const primaryIsCoach = /^coach\b/i.test(primaryName.trim());

    const filtered = companyActions.filter((a) => {
      if (!a || typeof a !== 'object') return false;

      // requires_faces (existing semantics, with backward-compat ids).
      if (typeof a.requires_faces === 'boolean') {
        if (a.requires_faces && !hasTaggedFaces) return false;
        if (!a.requires_faces && hasTaggedFaces) return false;
      } else if (a.id === 'home_supporters' || a.id === 'away_supporters') {
        if (hasTaggedFaces) return false;
      }

      // face_count gate — only meaningful when faces are tagged.
      if (a.face_count === 'one' && taggedFaceCount !== 1) return false;
      if (a.face_count === 'multi' && taggedFaceCount < 2) return false;

      // match_phase gate — chip only shows when the photo's phase is
      // known AND matches. If matchPhase is null (capture_date or match
      // times not yet loaded), hide phase-conditioned chips rather than
      // letting them leak through (e.g. "passing" on a pre-match shot).
      if (a.match_phase) {
        if (!matchPhase) return false;
        if (a.match_phase !== matchPhase) return false;
      }

      // requires_role gate — currently only 'coach' is supported, via
      // the "Coach " name-prefix convention. Defer to true when no
      // primary subject yet (filter narrows once a face is tagged).
      if (a.requires_role === 'coach' && !primaryIsCoach) return false;
      if (a.requires_role === 'player' && primaryIsCoach) return false;

      return true;
    });
    return [
      ...filtered.map((a) => ({ ...a, _kind: 'company' })),
      ...pinnedCaptions.map((p) => ({ ...p, _kind: 'pinned' })),
    ];
  }, [builderConfig, matchData, pinnedCaptions, hasTaggedFaces, taggedFaceCount, matchPhase, personTokenLists]);

  // Apply a quick-action chip. New phrase-form: resolve the action's
  // phrase against the current context, then substitute into the
  // company's caption_template at the {persons} placeholder so the rich
  // scaffolding (city / venue / season / date / country / photographer)
  // renders correctly. Falls back to the legacy `template` (full
  // caption) when the action lacks a phrase or the match has no
  // caption_template configured yet.
  const applyQuickAction = useCallback((action) => {
    if (!action) return;
    const captionTemplate = matchData?.iptc_config?.caption_template || '';
    const phrase = action.phrase || '';
    const legacyTemplate = action.template || '';
    let finalCaption = '';
    if (phrase && captionTemplate) {
      const resolvedPhrase = resolveTemplate(phrase);
      finalCaption = resolveTemplate(captionTemplate, { persons: resolvedPhrase });
    } else if (phrase) {
      finalCaption = resolveTemplate(phrase);
    } else if (legacyTemplate) {
      finalCaption = resolveTemplate(legacyTemplate);
    }
    if (!finalCaption) return;
    onCaptionChange(finalCaption, null);

    // Merge action.keywords into the photo's existing keyword set
    // (additive, deduped, case-insensitive). Empty list → no change.
    if (Array.isArray(action.keywords) && action.keywords.length && onKeywordsChange) {
      const existing = Array.isArray(existingKeywords) ? existingKeywords : [];
      const seen = new Set(existing.map((k) => String(k).toLowerCase()));
      const next = [...existing];
      for (const kw of action.keywords) {
        const trimmed = String(kw || '').trim();
        if (!trimmed) continue;
        const key = trimmed.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        next.push(trimmed);
      }
      if (next.length !== existing.length) onKeywordsChange(next);
    }
  }, [resolveTemplate, onCaptionChange, onKeywordsChange, matchData, existingKeywords]);

  // Remove a per-match pinned caption (× on the chip). Local-only pins
  // (id starts with "pin-local-") never reached the server, so just
  // drop them; otherwise fire the unpin PATCH in the background.
  // Either way we drop the pin from the in-memory cache so the chip
  // doesn't reappear after navigating to another photo.
  const removePinned = useCallback((cap) => {
    if (!cap?.id) return;
    setPinnedCaptions((prev) => prev.filter((p) => p.id !== cap.id));
    if (matchId) dropPendingPin(matchId, cap.id);
    if (!matchId) return;
    if (typeof cap.id === 'string' && cap.id.startsWith('pin-local-')) return;
    unpinMatchQuickCaption(matchId, cap.id).catch(() => {
      // Don't roll back — the user explicitly removed it locally.
    });
  }, [matchId]);

  // Manual pin from the footer when the auto-pin toggle is OFF.
  // Optimistic: chip appears immediately; server PATCH reconciles id.
  // Also pushed into the in-memory cache so the chip survives photo
  // navigation even if the server PATCH is still in flight.
  const pinCurrentCaption = useCallback((caption) => {
    if (!matchId || !caption) return;
    const label = caption.length > 80 ? `${caption.slice(0, 77)}…` : caption;
    const optimisticId = `pin-local-${Date.now()}`;
    const optimisticPin = { id: optimisticId, label, template: caption };
    setPinnedCaptions((prev) => [optimisticPin, ...prev.filter((p) => p.template !== caption)]);
    addPendingPin(matchId, optimisticPin);
    pinMatchQuickCaption(matchId, { label, template: caption })
      .then((pinned) => {
        if (!pinned) return;
        replacePendingPin(matchId, optimisticId, pinned);
        setPinnedCaptions((prev) =>
          prev.map((p) => (p.id === optimisticId ? pinned : p)),
        );
      })
      .catch(() => {
        /* keep the optimistic entry; the user can still use the chip. */
      });
  }, [matchId]);

  // Live caption from current chain
  const currentCaption = useMemo(() => buildCaption(chain, scoreStr, teamPrefixStrip), [chain, scoreStr, teamPrefixStrip]);
  const currentKeywords = useMemo(() => collectKeywords(chain), [chain]);

  // All keywords from saved + current
  const allKeywords = useMemo(() => {
    const kws = new Set();
    for (const sc of savedChains) {
      for (const kw of sc.keywords) kws.add(kw);
    }
    for (const kw of currentKeywords) kws.add(kw);
    return [...kws];
  }, [savedChains, currentKeywords]);

  // Combined caption from saved chains
  const combinedSavedCaption = useMemo(
    () => (savedChains.length > 0 ? savedChains.map((sc) => sc.caption).join('. ') + '.' : ''),
    [savedChains]
  );

  // Add a person to the chain
  const addPerson = useCallback((person) => {
    setChain((prev) => {
      const newChain = [...prev];
      if (newChain.length > 0) {
        newChain.push({ type: 'connector', text: '' });
      }
      newChain.push({ type: 'person', person, action: null });
      return newChain;
    });
    setActivePickerIndex(-1);
  }, []);

  // Set action on a person node
  const setAction = useCallback((nodeIndex, action) => {
    setChain((prev) => {
      const newChain = [...prev];
      newChain[nodeIndex] = { ...newChain[nodeIndex], action };
      return newChain;
    });
  }, []);

  // Remove a node (person + preceding connector, or connector + following person)
  const removeNode = useCallback((nodeIndex) => {
    setChain((prev) => {
      const newChain = [...prev];
      const node = newChain[nodeIndex];
      if (node.type === 'person') {
        // Remove person + the connector before it (if any)
        if (nodeIndex > 0 && newChain[nodeIndex - 1]?.type === 'connector') {
          newChain.splice(nodeIndex - 1, 2);
        } else if (nodeIndex === 0 && newChain[1]?.type === 'connector') {
          // First person: remove person + connector after it
          newChain.splice(0, 2);
        } else {
          newChain.splice(nodeIndex, 1);
        }
      }
      return newChain;
    });
    setActivePickerIndex(-1);
  }, []);

  // Remove connector + the person node after it
  const removeConnector = useCallback((connectorIndex) => {
    setChain((prev) => {
      const newChain = [...prev];
      // Remove connector + the next person node
      if (connectorIndex + 1 < newChain.length && newChain[connectorIndex + 1]?.type === 'person') {
        newChain.splice(connectorIndex, 2);
      } else {
        newChain.splice(connectorIndex, 1);
      }
      return newChain;
    });
    setActivePickerIndex(-1);
  }, []);

  // Update connector text
  const updateConnector = useCallback((connectorIndex, text) => {
    setChain((prev) => {
      const newChain = [...prev];
      newChain[connectorIndex] = { ...newChain[connectorIndex], text };
      return newChain;
    });
  }, []);

  // Get preceding action id for a connector (look at person node before it)
  const getPrecedingActionId = useCallback(
    (connectorIndex) => {
      if (connectorIndex > 0 && chain[connectorIndex - 1]?.type === 'person') {
        return chain[connectorIndex - 1].action?.id || null;
      }
      return null;
    },
    [chain]
  );

  // Save current chain
  const saveChain = useCallback(() => {
    if (!isChainComplete(chain)) return;
    const caption = buildCaption(chain, scoreStr, teamPrefixStrip);
    const keywords = collectKeywords(chain);
    setSavedChains((prev) => [...prev, { caption, keywords, chain: [...chain] }]);
    setChain([]);
    setActivePickerIndex(-1);
  }, [chain, scoreStr, teamPrefixStrip]);

  // Remove saved chain and persist the updated caption (or reset to template)
  const removeSavedChain = useCallback(async (index) => {
    const remaining = savedChains.filter((_, i) => i !== index);
    setSavedChains(remaining);

    if (remaining.length === 0) {
      // No chains left: reset to template
      await onCaptionChange(null, null);
      await onKeywordsChange(null);
      return;
    }

    // Rebuild caption from remaining chains
    const allKws = new Set();
    for (const sc of remaining) {
      for (const kw of sc.keywords) allKws.add(kw);
    }

    const chainPersonIds = new Set();
    for (const sc of remaining) {
      for (const node of sc.chain) {
        if (node.type === 'person') chainPersonIds.add(node.person.id);
      }
    }

    const firstMentionMap = new Map();
    for (const sc of remaining) {
      const firstPerson = sc.chain.find((n) => n.type === 'person');
      if (!firstPerson) continue;
      const pid = firstPerson.person.id;
      if (firstMentionMap.has(pid)) {
        firstMentionMap.set(pid, firstMentionMap.get(pid) + '. ' + sc.caption);
      } else {
        firstMentionMap.set(pid, sc.caption);
      }
    }

    const activeNames = (orderedNames || []).filter((n) => !n.softDeleted);
    const personsStr = activeNames.map((n) => n.label).join(', ');

    let finalCaption;
    if (renderedCaption && personsStr && renderedCaption.includes(personsStr)) {
      const parts = [];
      for (const name of activeNames) {
        if (firstMentionMap.has(name.person_id)) {
          parts.push(firstMentionMap.get(name.person_id));
        } else if (!chainPersonIds.has(name.person_id)) {
          parts.push(name.label);
        }
      }
      finalCaption = renderedCaption.replace(personsStr, parts.join(', '));
    } else {
      finalCaption = remaining.map((sc) => sc.caption).join('. ') + '.';
    }

    const serialized = serializeChains(remaining, [homeScore, awayScore]);
    await onCaptionChange(finalCaption, serialized);
    await onKeywordsChange([...allKws]);
  }, [savedChains, homeScore, awayScore, orderedNames, renderedCaption, onCaptionChange, onKeywordsChange]);

  // Apply all to image - replaces persons segment in rendered caption
  const applyAll = useCallback(async () => {
    // Auto-save current chain if complete
    const finalSaved = [...savedChains];
    if (isChainComplete(chain)) {
      const caption = buildCaption(chain, scoreStr, teamPrefixStrip);
      const keywords = collectKeywords(chain);
      finalSaved.push({ caption, keywords, chain: [...chain] });
    }

    if (finalSaved.length === 0) return;

    // Merge keywords
    const allKws = new Set(existingKeywords || []);
    for (const sc of finalSaved) {
      for (const kw of sc.keywords) allKws.add(kw);
    }

    // Collect all person IDs referenced in chains
    const chainPersonIds = new Set();
    for (const sc of finalSaved) {
      for (const node of sc.chain) {
        if (node.type === 'person') chainPersonIds.add(node.person.id);
      }
    }

    // Map first-mentioned person in each chain to that chain's caption text
    const firstMentionMap = new Map();
    for (const sc of finalSaved) {
      const firstPerson = sc.chain.find((n) => n.type === 'person');
      if (!firstPerson) continue;
      const pid = firstPerson.person.id;
      if (firstMentionMap.has(pid)) {
        firstMentionMap.set(pid, firstMentionMap.get(pid) + '. ' + sc.caption);
      } else {
        firstMentionMap.set(pid, sc.caption);
      }
    }

    // Build expected persons string from orderedNames (matches server {persons} render)
    const activeNames = (orderedNames || []).filter((n) => !n.softDeleted);
    const personsStr = activeNames.map((n) => n.label).join(', ');

    let finalCaption;
    if (renderedCaption && personsStr && renderedCaption.includes(personsStr)) {
      // Walk orderedNames spatially, replacing chain persons with chain text
      const parts = [];
      for (const name of activeNames) {
        if (firstMentionMap.has(name.person_id)) {
          parts.push(firstMentionMap.get(name.person_id));
        } else if (!chainPersonIds.has(name.person_id)) {
          parts.push(name.label);
        }
      }
      finalCaption = renderedCaption.replace(personsStr, parts.join(', '));
    } else {
      // Fallback: use chain text directly
      finalCaption = finalSaved.map((sc) => sc.caption).join('. ') + '.';
    }

    const serialized = serializeChains(finalSaved, [homeScore, awayScore]);
    await onCaptionChange(finalCaption, serialized);
    await onKeywordsChange([...allKws]);
    setChain([]);
    setSavedChains(finalSaved);
    setDrawerOpen(false);

    // Auto-pin the resolved caption to the match's quick-actions when
    // the toggle is on. Optimistic + fire-and-forget: the chip appears
    // immediately and the server PATCH reconciles in the background.
    // On flaky networks the chip stays visible even if the request
    // hangs / CORS-blocks; on success we swap in the server-assigned id.
    // The in-memory pins cache keeps the chip alive across photo
    // navigation in the same session.
    if (pinNextCaption && matchId && finalCaption) {
      const alreadyPinned = pinnedCaptions.some((p) => p.template === finalCaption);
      if (!alreadyPinned) {
        const label = finalCaption.length > 80
          ? `${finalCaption.slice(0, 77)}…`
          : finalCaption;
        const optimisticId = `pin-local-${Date.now()}`;
        const optimisticPin = { id: optimisticId, label, template: finalCaption };
        setPinnedCaptions((prev) => [optimisticPin, ...prev]);
        addPendingPin(matchId, optimisticPin);
        pinMatchQuickCaption(matchId, { label, template: finalCaption })
          .then((pinned) => {
            if (!pinned) return;
            replacePendingPin(matchId, optimisticId, pinned);
            setPinnedCaptions((prev) =>
              prev.map((p) => (p.id === optimisticId ? pinned : p)),
            );
          })
          .catch(() => {
            /* keep the optimistic entry; user can still apply / × it. */
          });
      }
    }
  }, [
    savedChains,
    chain,
    scoreStr,
    teamPrefixStrip,
    homeScore,
    awayScore,
    orderedNames,
    renderedCaption,
    existingKeywords,
    onCaptionChange,
    onKeywordsChange,
    matchId,
    pinNextCaption,
    pinnedCaptions,
  ]);

  const canSave = isChainComplete(chain);
  const canApply = savedChains.length > 0 || isChainComplete(chain);
  const chainCount = savedChains.length;

  // Build the chain rendering
  const chainElements = [];
  for (let i = 0; i < chain.length; i++) {
    const node = chain[i];
    if (node.type === 'person') {
      chainElements.push(
        <ChainNode
          key={`p-${i}`}
          node={node}
          index={i}
          activePickerIndex={activePickerIndex}
          onSetActivePicker={setActivePickerIndex}
          onActionSelect={setAction}
          onRemove={removeNode}
          teamPrefixStrip={teamPrefixStrip}
          actionCategories={actionCategories}
        />
      );
    } else if (node.type === 'connector') {
      chainElements.push(
        <ConnectorEditor
          key={`c-${i}`}
          text={node.text}
          precedingActionId={getPrecedingActionId(i)}
          onChange={(text) => updateConnector(i, text)}
          onRemove={() => removeConnector(i)}
          connectorSuggestions={connectorSuggestions}
        />
      );
    }
  }

  // Single chip render shared by the inline trigger row + the drawer.
  const renderQuickActionChip = (action) => {
    const label = (action.label || '')
      .replaceAll('{venue}', matchData?.venue || '{venue}')
      .replaceAll('{home}', matchData?.home_team_name || '{home}')
      .replaceAll('{away}', matchData?.away_team_name || '{away}');
    return (
      <span
        key={`${action._kind}:${action.id}`}
        className={clsx(
          styles.quickActionChip,
          action._kind === 'pinned' && styles.quickActionChipPinned,
        )}
      >
        <button
          type="button"
          className={styles.quickActionLabel}
          onClick={() => applyQuickAction(action)}
        >
          {label}
        </button>
        {action._kind === 'pinned' && (
          <button
            type="button"
            className={styles.quickActionRemove}
            onClick={() => removePinned(action)}
            aria-label="Remove pinned caption"
            title="Remove from quick actions"
          >
            {'×'}
          </button>
        )}
      </span>
    );
  };

  return (
    <>
      {/* Compact trigger in sidebar */}
      <div className={styles.triggerSection}>
        <div className={styles.triggerHeader}>
          <span className={styles.eyebrow}>Caption Builder</span>
          <button
            className={styles.buildBtn}
            onClick={() => setDrawerOpen(true)}
            disabled={disabled}
          >
            Build
          </button>
        </div>
        <MiniScore
          matchData={matchData}
          homeScore={homeScore}
          awayScore={awayScore}
          onHomeChange={setHomeScore}
          onAwayChange={setAwayScore}
          teamPrefixStrip={teamPrefixStrip}
        />
        {quickActions.length > 0 && (
          <div className={styles.quickActions}>
            {quickActions.map(renderQuickActionChip)}
          </div>
        )}
        {chainCount > 0 ? (
          <>
            <span className={styles.triggerInfo}>
              {chainCount} chain{chainCount !== 1 ? 's' : ''} built
            </span>
            <span className={styles.triggerPreview}>{combinedSavedCaption}</span>
          </>
        ) : (
          <span className={styles.triggerInfo}>No chains yet</span>
        )}
      </div>

      {/* Drawer */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <div className={styles.drawerHeader}>
          <span className={styles.drawerTitle}>Caption Builder</span>
          <button
            className={styles.closeBtn}
            onClick={() => setDrawerOpen(false)}
            title="Close"
          >
{'\u00d7'}
          </button>
        </div>

        <div className={styles.drawerBody}>
          {/* Score Ticker */}
          <ScoreTicker
            matchData={matchData}
            homeScore={homeScore}
            awayScore={awayScore}
            onHomeChange={setHomeScore}
            onAwayChange={setAwayScore}
            teamPrefixStrip={teamPrefixStrip}
          />

          {/* Player Pool */}
          <div>
            <div className={styles.sectionTitle}>Persons in image</div>
            <PlayerPool persons={persons} chain={chain} onAdd={addPerson} teamPrefixStrip={teamPrefixStrip} />
          </div>

          {/* Quick actions — company defaults + per-match pinned. */}
          {quickActions.length > 0 && (
            <div>
              <div className={styles.sectionTitle}>Quick actions</div>
              <div className={styles.quickActions}>
                {quickActions.map(renderQuickActionChip)}
              </div>
            </div>
          )}

          {/* Chain */}
          <div>
            <div className={styles.sectionTitle}>Current chain</div>
            {chain.length === 0 ? (
              <div className={styles.emptyChain}>
                Click a person above to start building a chain
              </div>
            ) : (
              <div className={styles.chain}>{chainElements}</div>
            )}
          </div>

          {/* Preview */}
          <div>
            <div className={styles.sectionTitle}>Preview</div>
            <CaptionPreview caption={currentCaption} keywords={allKeywords} grammarRules={grammarRules} />
          </div>

          {/* Saved Chains */}
          <SavedChainsList savedChains={savedChains} onRemove={removeSavedChain} />
        </div>

        <div className={styles.drawerFooter}>
          <label className={styles.pinToggle}>
            <input
              type="checkbox"
              checked={pinNextCaption}
              onChange={(e) => setPinNextCaption(e.target.checked)}
            />
            <span>Pin to quick actions</span>
          </label>
          {!pinNextCaption && canApply && (
            <button
              type="button"
              className={clsx(styles.footerBtn, styles.saveChainBtn)}
              onClick={() => pinCurrentCaption(currentCaption || combinedSavedCaption)}
              title="Pin the current caption without applying it"
            >
              + Add to quick actions
            </button>
          )}
          <button
            className={clsx(styles.footerBtn, styles.saveChainBtn)}
            onClick={saveChain}
            disabled={!canSave}
          >
            Save &amp; new chain
          </button>
          <button
            className={clsx(styles.footerBtn, styles.applyBtn)}
            onClick={applyAll}
            disabled={!canApply}
          >
            Apply to image
          </button>
        </div>
      </Drawer>
    </>
  );
}
